import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LanguageSelector } from "@/i18n";
import {
  SquaresFour, MapTrifold, ShieldCheck, SignOut, Password, Devices, DeviceMobile,
  DotsThreeCircle, Lifebuoy, ClipboardText, GearSix, CaretDown,
  IdentificationBadge, UsersThree, AddressBook, Eye, House,
} from "@phosphor-icons/react";

const C = { indigo: "#7C4DFF", indigoSoft: "#F1ECFF", indigoText: "#6A3EE8",
  ink: "#1B1B2F", sub: "#54565A", line: "#ECECF3", bg: "#E7EAF3" };
const GRAD = "linear-gradient(135deg,#8B5CF6 0%,#A855F7 100%)";
const MASTERBRAND = `${process.env.PUBLIC_URL || ""}/rotary-masterbrand.png`;

const PWA_ENABLED = process.env.REACT_APP_PWA_ENABLED === "true";
const ACCOUNT_ITEMS = [
  { to: "/account", label: "Account & Security", icon: ShieldCheck, testid: "account-menu-security" },
  { to: "/account#password", label: "Password", icon: Password, testid: "account-menu-password" },
  ...(PWA_ENABLED ? [
    { to: "/devices", label: "Devices and Access", icon: Devices, testid: "account-menu-devices" },
    { to: "/mobile-access", label: "Get Mobile Access", icon: DeviceMobile, testid: "account-menu-mobile" },
  ] : []),
];

function navFor(home) {
  const dash = { to: "/", label: "Dashboard", icon: SquaresFour, testid: "nav-dashboard" };
  switch (home) {
    case "district_ops":
    case "district_official":
      return { main: [dash], setup: null };
    case "district_admin":
      return { main: [dash], setup: { to: "/setup", label: "District GOV settings", icon: GearSix, testid: "nav-setup" } };
    case "club":
      return { main: [dash], setup: null };
    default:
      return { main: [dash], setup: null };
  }
}

function bottomNavFor(home) {
  // 4 primary zones, role-aware targets under the same labels.
  // "Visits" points at the club's GOV prep for club officers, else the dashboard (which
  // IS the district GOV visits overview) until a dedicated Visits screen exists.
  const visitsTo = home === "club" ? "/prep" : "/";
  return [
    { to: "/", label: "Home", icon: House, testid: "nav-home", match: (p) => p === "/" },
    { to: visitsTo, label: "Visits", icon: ClipboardText, testid: "nav-visits", match: (p) => /^\/(visits|gov|review|prep)(\/|$)/.test(p) },
    { to: "/directory", label: "Directory", icon: AddressBook, testid: "nav-directory", match: (p) => p.startsWith("/directory") },
    { to: "/profile", label: "Profile", icon: IdentificationBadge, testid: "nav-profile", match: (p) => p.startsWith("/profile") || p.startsWith("/account") },
  ];
}

const DEV_BYPASS = process.env.REACT_APP_DEV_AUTH_BYPASS === "true";

const navClass = "flex items-center gap-3 rounded-xl px-4 py-3 text-[16px] transition-all duration-200 hover:bg-[#F5F3FF]";
const navStyle = (isActive) => ({ background: isActive ? C.indigoSoft : "transparent", color: isActive ? C.indigoText : C.sub, fontWeight: isActive ? 600 : 500 });

export const AppShell = ({ children }) => {
  const { user, logout } = useAuth();
  const exitSupport = async () => {
    try { await api.post("/support/context/exit"); } catch (_) {}
    window.location.href = "/";
  };
  const exitViewAs = async () => {
    try { await api.post("/support/view-as/exit"); } catch (_) {}
    window.location.href = "/";
  };
  const location = useLocation();
  const [home, setHome] = useState(null);
  const [club, setClub] = useState(null);
  const [principal, setPrincipal] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const onClubPage = /^\/(prep|review|club)\//.test(location.pathname);

  useEffect(() => {
    (async () => {
      try {
        const [h, pr] = await Promise.all([api.get("/me/home"), api.get("/me/principal")]);
        setHome(h.data.home); setClub({ name: h.data.club_name, role: h.data.club_role }); setPrincipal(pr.data);
      } catch (_) {}
    })();
  }, []);
  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const { main, setup: setupFromHome } = navFor(home);
  const isOwner = principal?.is_platform_owner;
  const canSetup = isOwner || (principal?.district_roles || []).some((r) => r === "District Admin" || r === "District Secretary" || r === "District Governor");
  const setup = setupFromHome || (canSetup ? { to: "/setup", label: "Set up the year", icon: GearSix, testid: "nav-setup" } : null);
  const districtNo = principal?.district?.rotary_district_no
    || (principal?.district_id ? String(principal.district_id).replace(/^district-/, "") : null);
  const districtName = principal?.district?.name || (districtNo ? `District ${districtNo}` : null);
  const distRole = (principal?.district_roles && principal.district_roles[0]) || null;
  const districtLabel = distRole ? `${distRole}${districtNo ? " · District " + districtNo : ""}` : districtName;
  const clubLabel = home === "club" && club?.name ? `${club.role || "Club officer"} · ${club.name}` : null;
  const viewingAs = principal?.view_as;
  // Platform Owner on their own Support Hub — NOT inside a district context or viewing as a role.
  const ownerHome = isOwner && !viewingAs && !principal?.support_context;
  // The owner account may carry a residual district role/name; ignore it on the owner home.
  const cleanName = (user?.display_name || "").replace(/\s*\(.*\)/, "").trim();
  const nameLooksLikeRole = !cleanName || /governor|secretary|president|assistant|\bmember\b|\bowner\b|^district\b/i.test(cleanName);
  const ownerName = nameLooksLikeRole ? "Platform Owner" : cleanName;
  const roleLabel = ownerHome ? "Platform Owner" : (clubLabel || districtLabel || (isOwner ? "Platform Owner" : "Member"));
  const brandLabel = ownerHome ? "Rotary Operations Hub" : (districtNo ? `District ${districtNo}` : (districtName || "Rotary GOV"));
  // While an owner is viewing AS a role, show that person's identity — not the owner's own
  // account name — so the header never reads like two roles at once.
  const acctName = viewingAs ? (principal.view_as_name || roleLabel) : (ownerHome ? ownerName : (user?.display_name || user?.email));
  const initial = (acctName || "?").slice(0, 1).toUpperCase();

  const SetupLink = ({ mobile }) => setup && (
    <NavLink to={setup.to} data-testid={mobile ? `${setup.testid}-mobile` : setup.testid}
      title={setup.label} aria-label={setup.label}
      className={mobile
        ? "flex shrink-0 items-center justify-center rounded-xl p-2 transition-colors hover:bg-[#F5F3FF]"
        : "flex items-center gap-3 rounded-xl px-4 py-3 text-[16px] font-medium transition-colors hover:bg-[#F5F3FF]"}
      style={{ color: C.sub }}>
      <setup.icon size={20} weight="regular" />{!mobile && <> {setup.label}</>}
    </NavLink>
  );

  const AccountMenu = ({ mobile = false }) => (
    <div data-testid={mobile ? "account-menu-mobile" : "account-menu"}
      className={mobile
        ? "fixed bottom-20 inset-x-3 z-40 rounded-2xl bg-white p-2 shadow-2xl"
        : "absolute top-full left-0 right-0 mt-2 rounded-2xl bg-white p-2 shadow-2xl z-40"}
      style={{ boxShadow: "0 12px 40px rgba(28,30,60,0.18)" }}>
      {ACCOUNT_ITEMS.map(({ to, label, icon: Icon, testid }) => (
        <Link key={testid} to={to} data-testid={mobile ? `${testid}-m` : testid} onClick={() => setMenuOpen(false)}
          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium hover:bg-[#F5F3FF]" style={{ color: C.sub }}>
          <Icon size={18} weight="regular" /> {label}
        </Link>
      ))}
      {principal?.support_context && (
        <button data-testid={mobile ? "exit-support-m" : "exit-support"} onClick={exitSupport}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold hover:bg-[#FEF3E2]" style={{ color: "#C77700" }}>
          <SignOut size={18} weight="regular" /> Exit support context
        </button>
      )}
      <button data-testid={mobile ? "account-menu-signout-m" : "account-menu-signout"} onClick={logout}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold hover:bg-[#FDE9EC]" style={{ color: "#E5484D" }}>
        <SignOut size={18} weight="regular" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col" style={{ background: C.bg, color: C.ink }}>
      {/* Platform Owner support banner — always visible, on every screen, with a clear way home */}
      {principal?.view_as && (
        <div data-testid="view-as-banner"
          className="sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-2 text-white"
          style={{ background: GRAD }}>
          <span className="flex min-w-0 items-center gap-2 text-[13px] font-semibold">
            <Eye size={17} weight="fill" />
            <span className="truncate">Viewing as {principal.view_as_name || roleLabel} · support edit mode</span>
          </span>
          <button data-testid="exit-view-as-button" onClick={exitViewAs}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[12.5px] font-bold transition-colors hover:bg-white/30">
            <House size={15} weight="bold" /> Back to owner home
          </button>
        </div>
      )}
      {!principal?.view_as && principal?.support_context && (
        <div data-testid="support-context-banner"
          className="sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-2 text-white"
          style={{ background: C.indigo }}>
          <span className="flex min-w-0 items-center gap-2 text-[13px] font-semibold">
            <Lifebuoy size={17} weight="fill" />
            <span className="truncate">
              Inside District {principal.support_context.district_no || districtNo}
              {principal.support_context.mode === "edit" ? " · support edit" : " · view only"}
            </span>
          </span>
          <div className="flex shrink-0 items-center gap-2">
            {onClubPage && (
              <Link to="/" data-testid="back-to-district-button"
                className="flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[12.5px] font-bold transition-colors hover:bg-white/30">
                <SquaresFour size={15} weight="bold" /> District dashboard
              </Link>
            )}
            <button data-testid="exit-support-context-button" onClick={exitSupport}
              className="flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-[12.5px] font-bold transition-colors hover:bg-white/30">
              <SignOut size={15} weight="bold" /> Exit district
            </button>
          </div>
        </div>
      )}
      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Desktop top bar */}
        <header className="sticky top-0 z-30 hidden items-center gap-4 border-b px-6 py-2.5 md:flex"
          style={{ background: "rgba(245,246,251,0.92)", backdropFilter: "blur(12px)", borderColor: C.line }}>
          <NavLink to="/" className="flex items-center gap-3">
            <img src={MASTERBRAND} alt="Rotary" className="h-7 w-auto object-contain" />
            <span className="h-6 w-px" style={{ background: C.line }} />
            <p className="text-[15px] font-bold tracking-tight" style={{ color: C.ink }}>{brandLabel}</p>
          </NavLink>
          <nav className="ml-3 flex items-center gap-1">
            {main.map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={testid} to={to} end={to === "/"} data-testid={testid}
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-[14px] font-semibold transition-colors"
                style={({ isActive }) => (isActive ? { background: C.indigoSoft, color: C.indigoText } : { color: C.sub })}>
                {({ isActive }) => <><Icon size={18} weight={isActive ? "fill" : "regular"} /> {label}</>}
              </NavLink>
            ))}
            {isOwner && (
              <NavLink to="/support" data-testid="nav-support"
                className="flex items-center gap-2 rounded-xl px-3 py-2 text-[14px] font-semibold transition-colors"
                style={({ isActive }) => (isActive ? { background: C.indigoSoft, color: C.indigoText } : { color: C.sub })}>
                {({ isActive }) => <><Lifebuoy size={18} weight={isActive ? "fill" : "regular"} /> Support</>}
              </NavLink>
            )}
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            <LanguageSelector compact />
            <div className="relative" ref={menuRef}>
              <button data-testid="account-menu-toggle" onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 transition-colors hover:bg-[#F5F3FF]">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[15px] font-bold text-white" style={{ background: GRAD }}>{initial}</div>
                <div className="hidden min-w-0 text-left lg:block">
                  <p data-testid="user-display-name" className="truncate text-[14px] font-bold leading-tight" style={{ color: C.ink }}>{acctName}</p>
                  <p className="truncate text-[12px] font-medium" style={{ color: C.sub }}>{viewingAs ? "Viewing as this role" : roleLabel}</p>
                </div>
                <CaretDown size={15} weight="bold" style={{ color: C.sub }} className={menuOpen ? "rotate-180" : ""} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full z-40 mt-2 w-64 rounded-2xl border bg-white p-2 shadow-xl" style={{ borderColor: C.line }}>
                  <AccountMenu />
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-2 border-b px-4 md:hidden"
          style={{ background: "rgba(245,246,251,0.85)", backdropFilter: "blur(12px)", borderColor: C.line }}>
          <div className="flex min-w-0 items-center gap-2.5">
            <img src={MASTERBRAND} alt="Rotary" className="h-6 w-auto shrink-0 object-contain" />
            <span className="h-6 w-px shrink-0" style={{ background: C.line }} />
            <p className="truncate text-[14px] font-bold tracking-tight" style={{ color: C.ink }}>{brandLabel}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSelector compact />
            {DEV_BYPASS && (
              <span data-testid="dev-bypass-badge" title="Dev auth bypass active (preview only)"
                className="rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wide"
                style={{ background: "#FDE68A", color: "#92400E" }}>DEV</span>
            )}
            {(principal || isOwner) && (
              <span data-testid="role-badge-mobile"
                className="max-w-[28vw] truncate rounded-full px-2.5 py-1 text-[11.5px] font-bold"
                style={{ background: C.indigoSoft, color: C.indigoText }}>
                {roleLabel}
              </span>
            )}
            <button data-testid="account-menu-toggle-mobile" onClick={() => setMenuOpen((o) => !o)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[14px] font-bold text-white" style={{ background: GRAD }}>
              {initial}
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 pb-28 md:px-8 md:py-8 md:pb-10">
          {children}
        </main>

        <footer className="px-4 pb-24 pt-2 text-center md:px-8 md:pb-6" data-testid="app-footer">
          <p className="text-[12px] font-medium" style={{ color: C.sub }}>
            powered by <a href="https://commworks.online" target="_blank" rel="noopener noreferrer"
              className="font-semibold hover:underline" style={{ color: C.indigoText }}>commworks.online</a>
          </p>
        </footer>

        {/* Mobile bottom nav — 4 primary zones */}
        {menuOpen && <div className="md:hidden"><AccountMenu mobile /></div>}
        <nav className="fixed inset-x-0 bottom-0 z-30 flex rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
          style={{ boxShadow: "0 -4px 24px rgba(28,30,60,0.10)" }} data-testid="bottom-nav">
          {bottomNavFor(home).map(({ to, label, icon: Icon, testid, match }) => {
            const active = match(location.pathname);
            return (
              <Link key={testid} to={to} data-testid={`${testid}-mobile`}
                className="flex min-h-[60px] flex-1 flex-col items-center justify-center gap-1 py-2.5 text-[12px] font-semibold transition-colors"
                style={{ color: active ? C.indigoText : C.sub }}>
                <Icon size={23} weight={active ? "fill" : "regular"} /> {label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
